import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Shield, Database, Cookie, Mail, UserCheck, Globe, Lock } from 'lucide-react';

export default function PrivacyPage() {
  return (
    <div data-testid="privacy-page" className="min-h-screen bg-background">
      <div className="bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-12">
          <Badge className="bg-white/20 text-white border-0 mb-4">
            Protection des données
          </Badge>
          <h1 className="font-serif text-3xl md:text-4xl font-bold mb-4">
            Politique de confidentialité
          </h1>
          <p className="text-lg text-primary-foreground/90 max-w-3xl">
            Le JPRP s'engage à protéger la vie privée de ses utilisateurs conformément 
            au Règlement Général sur la Protection des Données (RGPD).
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 space-y-8">

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Database className="w-5 h-5 text-primary" />
              Responsable du traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le responsable du traitement des données personnelles est le Journal de pratiques 
              et recherches en psychothérapie (JPRP), représenté par son rédacteur en chef, 
              Victor Lopez-Hernandez.
            </p>
            <p>Contact : <a href="mailto:victor.loher@vmlh-tcc.fr" className="text-primary hover:underline">victor.loher@vmlh-tcc.fr</a></p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <UserCheck className="w-5 h-5 text-primary" />
              Données collectées
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 text-sm text-muted-foreground">
            <p>Dans le cadre du fonctionnement de la plateforme, nous collectons les données suivantes :</p>
            <ul className="list-disc ml-6 space-y-2">
              <li>
                <span className="font-medium text-foreground">Données d'inscription :</span> nom, 
                prénom, adresse e-mail, mot de passe (chiffré), institution d'affiliation, 
                identifiant ORCID (facultatif).
              </li>
              <li>
                <span className="font-medium text-foreground">Données de soumission :</span> manuscrits, 
                métadonnées des articles (titre, résumé, mots-clés), fichiers téléversés.
              </li>
              <li>
                <span className="font-medium text-foreground">Données de navigation :</span> adresse IP, 
                type de navigateur, pages consultées, à des fins de statistiques anonymisées.
              </li>
              <li>
                <span className="font-medium text-foreground">Newsletter :</span> adresse e-mail des 
                abonnés à la lettre d'information.
              </li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Finalités du traitement
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>Les données personnelles sont collectées pour les finalités suivantes :</p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Gestion des comptes utilisateurs et authentification</li>
              <li>Traitement éditorial des manuscrits soumis (soumission, évaluation par les pairs, publication)</li>
              <li>Attribution de DOI et génération de citations</li>
              <li>Envoi de notifications relatives au processus éditorial</li>
              <li>Envoi de la newsletter (avec consentement explicite)</li>
              <li>Établissement de statistiques anonymisées d'utilisation</li>
            </ul>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              Base juridique et durée de conservation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le traitement des données repose sur le <span className="font-medium text-foreground">consentement</span> de 
              l'utilisateur (inscription, newsletter) et sur l'<span className="font-medium text-foreground">intérêt 
              légitime</span> du JPRP (gestion éditoriale, statistiques).
            </p>
            <p>
              Les données des comptes utilisateurs sont conservées tant que le compte est actif. 
              En cas de suppression du compte, les données personnelles sont effacées dans un 
              délai de 30 jours, à l'exception des données liées aux articles publiés 
              (nom d'auteur, affiliation) qui font partie intégrante du registre scientifique.
            </p>
            <p>
              Les données de la newsletter sont conservées jusqu'au désabonnement de l'utilisateur.
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              Droits des utilisateurs
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Conformément au RGPD (articles 15 à 22), vous disposez des droits suivants 
              sur vos données personnelles :
            </p>
            <ul className="list-disc ml-6 space-y-1">
              <li><span className="font-medium text-foreground">Droit d'accès :</span> obtenir une copie de vos données personnelles</li>
              <li><span className="font-medium text-foreground">Droit de rectification :</span> corriger des données inexactes ou incomplètes</li>
              <li><span className="font-medium text-foreground">Droit à l'effacement :</span> demander la suppression de vos données</li>
              <li><span className="font-medium text-foreground">Droit à la portabilité :</span> recevoir vos données dans un format structuré</li>
              <li><span className="font-medium text-foreground">Droit d'opposition :</span> vous opposer au traitement de vos données</li>
              <li><span className="font-medium text-foreground">Droit de retrait du consentement :</span> retirer votre consentement à tout moment</li>
            </ul>
            <p>
              Pour exercer ces droits, contactez-nous à l'adresse : 
              <a href="mailto:victor.loher@vmlh-tcc.fr" className="text-primary hover:underline">victor.loher@vmlh-tcc.fr</a>
            </p>
            <p>
              En cas de litige, vous pouvez introduire une réclamation auprès de la 
              Commission Nationale de l'Informatique et des Libertés (CNIL).
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-serif text-xl flex items-center gap-2">
              <Cookie className="w-5 h-5 text-primary" />
              Cookies
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm text-muted-foreground">
            <p>
              Le JPRP utilise uniquement des <span className="font-medium text-foreground">cookies 
              essentiels</span> au fonctionnement de la plateforme :
            </p>
            <ul className="list-disc ml-6 space-y-1">
              <li>Cookie de session pour maintenir votre connexion</li>
              <li>Cookie de préférences pour mémoriser vos choix d'affichage</li>
            </ul>
            <p>
              Aucun cookie publicitaire ou de suivi à des fins commerciales n'est utilisé.
            </p>
          </CardContent>
        </Card>

        <div className="text-xs text-muted-foreground text-center pt-4">
          Dernière mise à jour : février 2026
        </div>
      </div>
    </div>
  );
}
